import { useState } from "react";
import type { JSX } from "react";
import {
  addTagPath,
  formatTagPath,
  normalizeRecipeTags,
  parseTagPathInput,
  removeTagPath,
  type RecipeTag
} from "../../shared/recipe-tags";

type RecipeTagsEditorProps = {
  tags: RecipeTag[] | undefined;
  suggestions?: readonly string[][];
  onChange: (tags: RecipeTag[]) => void;
};

export function RecipeTagsEditor({ tags, suggestions = [], onChange }: RecipeTagsEditorProps): JSX.Element {
  const [draft, setDraft] = useState("");
  const normalized = normalizeRecipeTags(tags);

  function addPath(path: readonly string[]): void {
    onChange(addTagPath(normalized, path));
    setDraft("");
  }

  function onAddDraft(): void {
    const path = parseTagPathInput(draft);
    if (path === null) {
      return;
    }
    addPath(path);
  }

  const unusedSuggestions = suggestions.filter(
    (path) => !normalized.some((tag) => formatTagPath(tag.path) === formatTagPath(path))
  );

  return (
    <div className="recipe-tags-editor">
      <div className="recipe-tag-list" role="list" aria-label="Recipe tags">
        {normalized.map((tag) => (
          <span key={formatTagPath(tag.path)} className="recipe-tag-chip" role="listitem">
            {formatTagPath(tag.path)}
            <button
              type="button"
              className="recipe-tag-remove"
              aria-label={`Remove tag ${formatTagPath(tag.path)}`}
              onClick={() => { onChange(removeTagPath(normalized, tag.path)); }}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="recipe-tag-add-row">
        <input
          className="text-input"
          value={draft}
          placeholder="Desserts > Cakes"
          aria-label="Add tag"
          list={unusedSuggestions.length > 0 ? "recipe-tag-suggestions" : undefined}
          onChange={(event) => { setDraft(event.target.value); }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onAddDraft();
            }
          }}
        />
        <button className="secondary-action" type="button" onClick={onAddDraft}>
          Add tag
        </button>
      </div>
      {unusedSuggestions.length > 0 ? (
        <datalist id="recipe-tag-suggestions">
          {unusedSuggestions.map((path) => (
            <option key={formatTagPath(path)} value={formatTagPath(path)} />
          ))}
        </datalist>
      ) : null}
    </div>
  );
}
